import { format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { parseError } from '@/lib/errors'
import { formatCurrency } from '@/lib/formatters'
import {
  DealValueSource,
  type PipelineDeal,
  PipelinePageStage,
  useUpdateLeadPipelineFields,
} from '@/models/myPipeline'

function sourceLabel(deal: PipelineDeal): string {
  if (deal.dealValueSource === DealValueSource.MANUAL) return 'set by you'
  if (deal.pageStage === PipelinePageStage.WON) return 'from bound policy'
  return `from quote · ${Math.round(Number(deal.weight) * 100)}%`
}

/** Right-aligned deal value; click it to override the auto-filled number, or empty it to go back to auto. */
export function DealValueCell({ deal }: { deal: PipelineDeal }) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(deal.dealValue ?? '')
  const update = useUpdateLeadPipelineFields()

  const save = () => {
    const normalized = draft.trim()
    if (normalized === (deal.dealValue ?? '')) {
      setIsEditing(false)
      return
    }
    update.mutate(
      normalized
        ? { id: deal.leadId, dealValue: normalized }
        : { id: deal.leadId, dealValueSource: DealValueSource.AUTO },
      {
        onSuccess: () => setIsEditing(false),
        onError: (error) => toast.error(parseError(error)),
      }
    )
  }

  if (isEditing) {
    return (
      <Input
        autoFocus
        inputMode="decimal"
        value={draft}
        disabled={update.isPending}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save()
          if (event.key === 'Escape') setIsEditing(false)
        }}
        placeholder="Auto"
        className="h-7 w-28 text-right text-sm tabular-nums"
        aria-label={`Deal value for ${deal.legalName}`}
      />
    )
  }

  // An endorsement row has no lead field of its own to override.
  if (deal.isEndorsement) {
    return (
      <div className="text-right">
        <div className="text-sm font-medium tabular-nums">{formatCurrency(Number(deal.dealValue))}</div>
        <div className="text-muted-foreground text-xs">from quote</div>
      </div>
    )
  }

  if (deal.dealValue === null) {
    return (
      <div className="text-right">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground h-auto border border-dashed px-2 py-0.5 text-xs"
          onClick={() => {
            setDraft('')
            setIsEditing(true)
          }}
        >
          Set value
        </Button>
      </div>
    )
  }

  return (
    <div className="text-right">
      <Button
        variant="ghost"
        size="sm"
        className="h-auto px-1 py-0 text-sm font-medium tabular-nums"
        onClick={() => {
          setDraft(deal.dealValue ?? '')
          setIsEditing(true)
        }}
      >
        {formatCurrency(Number(deal.dealValue))}
      </Button>
      <div className="text-muted-foreground text-xs">{sourceLabel(deal)}</div>
    </div>
  )
}

export function carriedFromLabel(carriedFrom: string): string {
  return `Carried from ${format(new Date(`${carriedFrom}T00:00:00`), 'MMMM')}`
}
